import React, { useRef, useState, useEffect } from "react";
import { IconFile, Icons, tokens } from "@/shared";
import {
    FileUploadContainer,
    DropZone,
    Text,
    SubText,
    FilePreview,
    FileIconWrapper,
    FileDetails,
    FileMeta,
    FileName,
    FileSize,
    RemoveButton,
    ProgressBarContainer,
    ProgressFill,
} from "./styles";

export interface FileUploadProps {
    onFileSelect: (file: File) => void;
    onRemoveFile?: () => void;
    selectedFile?: File | null;
    accept?: string;
    maxSize?: number;
    disabled?: boolean;
    placeholder?: string;
    subtext?: string;
}

const DEFAULT_MAX_SIZE = 15 * 1024 * 1024;
const DEFAULT_ACCEPT = "image/jpeg,image/jpg,image/png,application/pdf";

export const FileUpload: React.FC<FileUploadProps> = ({
    onFileSelect,
    onRemoveFile,
    selectedFile,
    accept = DEFAULT_ACCEPT,
    maxSize = DEFAULT_MAX_SIZE,
    disabled = false,
    placeholder = "Click aquí para subir o arrastrar los archivos",
    subtext = "Se admite JPG/JPEG, PDF, PNG hasta 15mb",
}) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (selectedFile) {
            // Reset progress when a distinct file is selected
            setUploadProgress(0);

            // Simulate upload progress
            const interval = setInterval(() => {
                setUploadProgress((prev) => {
                    if (prev >= 100) {
                        clearInterval(interval);
                        return 100;
                    }
                    return prev + 10;
                });
            }, 100);

            return () => clearInterval(interval);
        } else {
            setUploadProgress(0);
        }
    }, [selectedFile]);

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setIsDragActive(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);

        if (disabled) return;

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            validateAndSelect(files[0]);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            validateAndSelect(files[0]);
        }
    };

    const validateAndSelect = (file: File) => {
        if (file.size > maxSize) {
            return;
        }

        const allowedTypes = accept.split(",").map((t) => t.trim());
        if (allowedTypes.length > 0 && !allowedTypes.some(type => {
            if (type.endsWith('/*')) {
                const baseType = type.split('/')[0];
                return file.type.startsWith(baseType);
            }
            if (type.startsWith('.')) {
                return file.name.toLowerCase().endsWith(type.toLowerCase());
            }
            return file.type === type;
        })) {
            return;
        }

        onFileSelect(file);
    };

    const handleRemove = () => {
        if (onRemoveFile) {
            onRemoveFile();
        }
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };

    const handleClick = () => {
        if (!disabled && inputRef.current) {
            inputRef.current.click();
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const isUploading = uploadProgress < 100;

    return (
        <FileUploadContainer>
            {!selectedFile ? (
                <DropZone
                    onClick={handleClick}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    isDragActive={isDragActive}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        accept={accept}
                        onChange={handleInputChange}
                        style={{ display: "none" }}
                        disabled={disabled}
                    />
                    <Text>{placeholder}</Text>
                    <SubText>{subtext}</SubText>
                </DropZone>
            ) : (
                <FilePreview>
                    <FileIconWrapper>
                        <IconFile width={24} height={24} />
                    </FileIconWrapper>

                    <FileDetails>
                        <FileMeta>
                            <FileName title={selectedFile.name}>{selectedFile.name}</FileName>
                            {!isUploading && (
                                <FileSize>• {formatFileSize(selectedFile.size)}</FileSize>
                            )}
                        </FileMeta>

                        {isUploading && (
                            <ProgressBarContainer>
                                <ProgressFill style={{ width: `${uploadProgress}%` }} />
                            </ProgressBarContainer>
                        )}
                    </FileDetails>

                    {onRemoveFile && (
                        <RemoveButton onClick={handleRemove} type="button">
                            <Icons name="iconClose" width={16} height={16} color={tokens.colors.basics[1900]} />
                        </RemoveButton>
                    )}
                </FilePreview>
            )}
        </FileUploadContainer>
    );
};
